import React, { useState } from 'react';
import { Button, Grid, makeStyles, Theme } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ExploreOutlinedIcon from '@material-ui/icons/ExploreOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import { useHistory } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import HeaderActions from '../../components/layout/HeaderActions';
import { localRoutes } from '../../data/constants';
import { colors } from '../../theme/tokens';
import { fontFamilies } from '../../theme/fonts';
import { gradientText } from '../../theme/gradientText';

// youtu.be/btpSZmnFqgo — the id is the part after the last slash, which is
// what both the embed and the thumbnail URLs below are built from.
const WELCOME_VIDEO_ID = 'btpSZmnFqgo';

const CORAL = colors.progressFill;
const ORANGE = colors.accentOrange;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    // Layout's content area pads 24px top + bottom (theme.spacing(3)); fill
    // the rest of the viewport so the hero reads as a full first screen,
    // matching the shared design.
    minHeight: 'calc(100vh - 48px)',
    paddingTop: 8,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  portalLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
  },
  topBarRight: { display: 'flex', alignItems: 'center', gap: 20 },
  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: colors.textSecondary,
    cursor: 'pointer',
    '&:hover': { color: colors.textPrimary },
  },

  hero: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 28,
    flex: 1,
    background: colors.bgSurface,
    border: `1px solid ${colors.border}`,
    borderRadius: 20,
    padding: '36px 36px',
    marginBottom: 24,
    [theme.breakpoints.down('sm')]: { flexDirection: 'column', padding: 22 },
  },
  heroLeft: {
    flex: '1 1 46%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as any,
    color: CORAL,
    marginBottom: 14,
  },
  heroTitle: {
    fontFamily: fontFamilies.display,
    fontSize: 'clamp(2rem, 1.5rem + 2vw, 3rem)',
    fontWeight: 800,
    lineHeight: 1.08,
    color: colors.textPrimary,
    margin: 0,
  },
  heroTitleAccent: { ...gradientText },
  heroTagline: {
    fontSize: 18,
    fontWeight: 600,
    color: colors.textPrimary,
    margin: '18px 0 10px',
  },
  heroBody: {
    fontSize: 14,
    lineHeight: 1.7,
    color: colors.textSecondary,
    maxWidth: 440,
    marginBottom: 26,
  },
  ctaBtn: {
    alignSelf: 'flex-start' as any,
    background: `linear-gradient(90deg, ${CORAL} 0%, ${ORANGE} 100%)`,
    color: colors.onStat,
    borderRadius: 10,
    padding: '12px 24px',
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none' as any,
    boxShadow: 'none',
    '&:hover': { opacity: 0.92, boxShadow: 'none' },
  },
  ctaSub: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 10,
  },

  heroRight: {
    flex: '1 1 54%',
    position: 'relative' as any,
    borderRadius: 16,
    overflow: 'hidden',
    minHeight: 320,
    background: colors.bgSubtle,
  },
  videoFrame: {
    position: 'absolute' as any,
    inset: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  videoThumb: {
    position: 'absolute' as any,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  playBtn: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    '& svg': { fontSize: 28, color: CORAL, marginLeft: 3 },
  },

  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 16,
    flexWrap: 'wrap' as any,
    gap: 6,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: colors.textPrimary,
  },
  sectionHint: { fontSize: 12, color: colors.textMuted },

  card: {
    background: colors.bgSurface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: '20px 20px',
    height: '100%',
  },
  cardIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: colors.tintRed,
    color: CORAL,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
    '& svg': { fontSize: 19 },
  },
  cardEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as any,
    color: colors.textMuted,
    marginBottom: 6,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 8,
  },
  cardBody: { fontSize: 13, lineHeight: 1.6, color: colors.textSecondary },
}));

const FEATURE_CARDS = [
  {
    eyebrow: 'Explore',
    title: 'Explore your possibilities',
    body: "Discover your strengths and find the learning path that's right for you.",
    icon: ExploreOutlinedIcon,
  },
  {
    eyebrow: 'Build',
    title: 'Build something that matters',
    body: "Turn new skills into real projects and a portfolio you're proud to share.",
    icon: BuildOutlinedIcon,
  },
  {
    eyebrow: 'Launch',
    title: 'Launch your next chapter',
    body: 'Connect with opportunities and take your next step with confidence.',
    icon: TrendingUpOutlinedIcon,
  },
];

/**
 * Landing screen for a student who hasn't enrolled in anything yet —
 * shown instead of the full dashboard until their first enrollment is
 * approved. See NewStudentGate for when this renders.
 */
const StudentHome = () => {
  const classes = useStyles();
  const history = useHistory();
  const [playing, setPlaying] = useState(false);

  return (
    <Layout bare>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <span className={classes.portalLabel}>Student portal</span>
          <div className={classes.topBarRight}>
            <div className={classes.helpLink}>
              <HelpOutlineIcon style={{ fontSize: 17 }} />
              Need a hand?
            </div>
            <HeaderActions />
          </div>
        </div>

        <div className={classes.hero}>
          <div className={classes.heroLeft}>
            <div className={classes.eyebrow}>Your journey starts here</div>
            <h1 className={classes.heroTitle}>
              Welcome to
              <br />
              <span className={classes.heroTitleAccent}>Elevate Academy</span>
            </h1>
            <div className={classes.heroTagline}>
              Big dreams. Real skills. Your next chapter.
            </div>
            <div className={classes.heroBody}>
              Your space to learn, build your portfolio, and discover what's
              next. Everything you need to move forward, together.
            </div>
            <Button
              className={classes.ctaBtn}
              endIcon={<ArrowForwardIcon />}
              onClick={() => history.push(localRoutes.catalog)}
            >
              Explore Courses
            </Button>
            <div className={classes.ctaSub}>
              Your potential is the beginning. Let's see where it takes you.
            </div>
          </div>

          <div className={classes.heroRight}>
            {playing ? (
              <iframe
                className={classes.videoFrame}
                src={`https://www.youtube.com/embed/${WELCOME_VIDEO_ID}?autoplay=1`}
                title="Welcome to Elevate Academy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div
                className={classes.videoThumb}
                style={{
                  backgroundImage: `url(https://img.youtube.com/vi/${WELCOME_VIDEO_ID}/maxresdefault.jpg)`,
                }}
                onClick={() => setPlaying(true)}
                role="button"
              >
                <div className={classes.playBtn}>
                  <PlayArrowIcon />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={classes.sectionHead}>
          <div className={classes.sectionTitle}>
            One portal. Every step of your journey.
          </div>
          <div className={classes.sectionHint}>
            From curiosity to opportunity
          </div>
        </div>

        <Grid container spacing={2}>
          {FEATURE_CARDS.map(({ eyebrow, title, body, icon: Icon }) => (
            <Grid item xs={12} sm={4} key={eyebrow}>
              <div className={classes.card}>
                <div className={classes.cardIcon}>
                  <Icon />
                </div>
                <div className={classes.cardEyebrow}>{eyebrow}</div>
                <div className={classes.cardTitle}>{title}</div>
                <div className={classes.cardBody}>{body}</div>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    </Layout>
  );
};

export default StudentHome;
